import React, { useEffect, useRef, useState } from 'react'
import { CITIES } from '../data/content'
import AugustTexture from './AugustTexture'
import ThemeToggleButton from './ThemeToggleButton'
import './AugustSplash.css'

const SEQUENCE = [
  { word: 'Thank you', city: 'New York', color: '#EE352E' },
  { word: 'Merci',     city: 'Paris',    color: '#5F2D8A' },
  { word: 'Thank you', city: 'London',   color: '#E32017' },
  { word: 'Grazie',    city: 'Milano',   color: '#CC0000' },
  { word: 'Gracias',   city: 'Madrid',   color: '#E4032E' },
  { word: 'Danke',     city: 'Berlin',   color: '#F0CA00' },
]

// Original lines — do NOT replace with the reference mockup's lyrics (copyrighted).
// Exported so AugustAuthPage can reuse the same wallpaper/texture positions.
export const WALLPAPER_LINES = [
  "Thank you for the slow coffee and the open window",
  "The metro pulled in right as I reached the platform",
  "Ten minutes of sun on the balcony before the call",
  "Rain on the awning while I wrote this down",
  "Day seven and it is starting to feel like a habit",
  "The bakery smell all the way down the street",
  "Grateful for the call I almost did not make",
  "Wrote three things and felt lighter after",
  "The light in August is softer somehow",
  "One small thank you at a time",
]

export const RINGS = [
  { width: 90, height: 90, top: -20, right: 30, opacity: 0.7 },
  { width: 60, height: 60, top: -5,  right: 55, opacity: 0.4 },
  { width: 70, height: 70, bottom: 60, left: -15, opacity: 0.5 },
]

export const STATIC_SPLATS = [
  { width: 14, height: 12, top: 72,  left: 22, opacity: 0.45, borderRadius: '60% 40% 55% 45%' },
  { width: 8,  height: 8,  top: 80,  left: 38, opacity: 0.35, borderRadius: '50%' },
  { width: 5,  height: 5,  top: 68,  left: 48, opacity: 0.3,  borderRadius: '50%' },
  { width: 12, height: 14, top: 290, right: 24, opacity: 0.4,  borderRadius: '45% 55% 50% 50%' },
  { width: 5,  height: 5,  top: 306, right: 20, opacity: 0.28, borderRadius: '50%' },
  { width: 16, height: 11, bottom: 95, left: 20, opacity: 0.42, borderRadius: '55% 45% 48% 52%' },
  { width: 6,  height: 6,  bottom: 108, left: 38, opacity: 0.3,  borderRadius: '50%' },
]

const BURST_DOTS = [
  { width: 11, height: 11, top: 195, left: 38 },
  { width: 6,  height: 6,  top: 202, left: 52 },
  { width: 4,  height: 4,  top: 188, left: 46 },
  { width: 8,  height: 8,  top: 208, left: 32 },
  { width: 5,  height: 5,  top: 182, left: 62 },
  { width: 13, height: 13, top: 200, right: 42 },
  { width: 7,  height: 7,  top: 190, right: 32 },
  { width: 4,  height: 4,  top: 210, right: 54 },
  { width: 3,  height: 3,  top: 215, right: 38 },
]

function AugustSplash({ onDone, augustTheme, onAugustThemeChange }) {
  const reduced = useRef(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  ).current

  const [show, setShow] = useState({
    wm: reduced, burst: false, sub: reduced, cta: reduced, whisper: reduced, exit: false,
  })

  useEffect(() => {
    if (reduced) return // everything already visible via initial state; wait for tap
    const timers = []
    const after = (ms, fn) => timers.push(setTimeout(fn, ms))

    after(250,  () => setShow(s => ({ ...s, wm: true })))
    after(700,  () => setShow(s => ({ ...s, burst: true })))
    after(850,  () => setShow(s => ({ ...s, sub: true })))
    after(1250, () => setShow(s => ({ ...s, cta: true })))
    after(1850, () => setShow(s => ({ ...s, whisper: true })))

    return () => timers.forEach(clearTimeout)
  }, [reduced])

  const handleBegin = () => {
    if (show.exit) return
    setShow(s => ({ ...s, exit: true }))
    setTimeout(onDone, reduced ? 0 : 500) // reduced motion: no fade to wait for
  }

  return (
    <div className={`aug-splash${show.exit ? ' aug-splash--exit' : ''}`} data-city="august">
      <AugustTexture variant="legacy" wallpaperLines={WALLPAPER_LINES} rings={RINGS} splats={STATIC_SPLATS} lineCount={20} />
      <ThemeToggleButton augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange} className="aug-theme-toggle--corner" />

      {BURST_DOTS.map((d, i) => (
        <div
          key={i}
          className={`aug-splash-splat-a${show.burst ? ' burst' : ''}`}
          style={{ ...d, '--d': `${i * 22}ms` }}
        />
      ))}

      <div className="aug-splash-content-glow" />

      <div className="aug-splash-content">
        <div className={`aug-splash-wordmark${show.wm ? ' show' : ''}`}>Magic</div>
        <div className={`aug-splash-subtitle${show.sub ? ' show' : ''}`}>28 tracks of gratitude, reflection and meaningful moments.</div>
        <button className={`aug-splash-cta${show.cta ? ' show' : ''}`} onClick={handleBegin}>Press Play</button>
        <div className={`aug-splash-whisper${show.whisper ? ' show' : ''}`}>One track a day</div>
      </div>
    </div>
  )
}

export default function Splash({ onDone, theme, city, augustTheme, onAugustThemeChange }) {
  if (city === 'august') return <AugustSplash onDone={onDone} augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange} />
  return <ClassicSplash onDone={onDone} theme={theme} city={city} />
}

function ClassicSplash({ onDone, theme, city }) {
  const [phase, setPhase] = useState('enter')
  const [cardIdx, setCardIdx] = useState(0)
  const cityData = CITIES[city] || CITIES.nyc
  const lineColor = cityData.primaryColor

  useEffect(() => {
    const CARD = 480
    const timers = []
    timers.push(setTimeout(() => setPhase('board'), 80))
    for (let i = 1; i < SEQUENCE.length; i++) {
      timers.push(setTimeout(() => setCardIdx(i), 80 + i * CARD))
    }
    const revealAt = 80 + SEQUENCE.length * CARD  // 2960ms
    timers.push(setTimeout(() => setPhase('reveal'), revealAt))
    timers.push(setTimeout(() => setPhase('exit'),   revealAt + 1400))
    timers.push(setTimeout(() => onDone(),           revealAt + 2000))
    return () => timers.forEach(clearTimeout)
  }, [onDone])

  const card     = SEQUENCE[cardIdx]
  const isGold   = card.color === '#F0CA00'
  const fg       = isGold ? '#111' : '#fff'
  const fgMute   = isGold ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.55)'
  const showBoard    = phase === 'board'
  const showWordmark = phase === 'reveal' || phase === 'exit'
  const outerMod     = phase === 'enter' ? 'enter' : phase === 'exit' ? 'exit' : 'show'

  return (
    <div className={`splash-new splash-new--${outerMod}`}>

      {/* Full-screen colored bg for board phase */}
      <div className="splash-board-bg" style={{ background: card.color, opacity: showBoard ? 1 : 0 }}/>

      {/* Cycling thank-you board */}
      {showBoard && (
        <div className="splash-board">
          <div key={cardIdx} className="splash-board-card">
            <div className="splash-board-dot-top" style={{ background: fg }}/>
            <div className="splash-board-word" style={{ color: fg }}>{card.word}</div>
            <div className="splash-board-city" style={{ color: fgMute }}>{card.city.toUpperCase()}</div>
          </div>
          <div className="splash-board-dots">
            {SEQUENCE.map((_, i) => (
              <div key={i} className="splash-board-progress-dot"
                style={{
                  background: i === cardIdx ? fg : fgMute,
                  transform: i === cardIdx ? 'scale(1.5)' : 'scale(1)',
                }}/>
            ))}
          </div>
        </div>
      )}

      {/* Wordmark reveal */}
      {showWordmark && (
        <>
          <div className="splash-glow" style={{ background: lineColor }}/>
          <div className="splash-stripe"/>
          <div className="splash-new-inner">
            <div className="splash-wordmark">The Magic</div>
            <div className="splash-line" style={{ background: lineColor }}/>
          </div>
        </>
      )}
    </div>
  )
}
